import type { UserQuestionAnswer } from '@/frontend/pages/Dashboard/AgentChats/types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { ArrowLeft, MessagesSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import { ConversationPanel } from '@/frontend/pages/Dashboard/AgentChats/components/ConversationPanel'
import { publishAgentChatSidebar } from '@/frontend/pages/Dashboard/AgentChats/agentChatSidebarStore'
import { isAssistantUiEnabled, persistedUiParam } from '@/frontend/pages/Dashboard/AgentChats/runtime/assistantUiToggle'
import { validateAgentChatFiles } from '@/frontend/pages/Dashboard/AgentChats/attachments'
import type { AgentChatsPageProps } from '@/frontend/pages/Dashboard/AgentChats/types'
import { useAgentChatController } from '@/frontend/pages/Dashboard/AgentChats/useAgentChatController'
import { latestContextUsage } from '@/frontend/features/AgentChats/assistant-ui/contextUsage'
import type { SharedProps } from '@/frontend/types/inertia'

const AssistantConversationPanel = React.lazy(() =>
  import('@/frontend/features/Fde/LiveAssistantConversationPanel')
    .then((module) => ({ default: module.LiveAssistantConversationPanel })),
)

const AgentChatsIndex = ({
  page_title,
  organization_id,
  digital_worker,
  session: initialSession,
  sessions: initialSessions,
  projects: initialProjects,
}: AgentChatsPageProps) => {
  const { auth, navigableResources } = usePage<SharedProps>().props
  const currentUserName = auth.user?.name || null
  // Locked once per mount: assistant-ui is the default layout (SZ-1491 cutover);
  // `?ui=legacy` opts back into the legacy panel. See assistantUiToggle.ts.
  const [useAssistantUi] = useState(isAssistantUiEnabled)
  const [draft, setDraft] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const recoveredDraftTokenRef = useRef<symbol | null>(null)

  const updateUrlForSession = useCallback((sessionId: string | null) => {
    if (typeof window === 'undefined' || !digital_worker) return

    const params = new URLSearchParams({ digital_worker_id: digital_worker.id })
    if (sessionId) {
      params.set('session_id', sessionId)
    } else {
      params.set('new_conversation', '1')
    }
    const ui = persistedUiParam()
    if (ui) params.set('ui', ui)

    // A late Inertia response from the previous conversation must not reclaim
    // the page after the user explicitly detached into a blank conversation.
    if (!sessionId) router.cancel()
    window.history.pushState(window.history.state, '', `/dashboard/agent_chats?${params.toString()}`)
  }, [digital_worker])

  const chat = useAgentChatController({
    organizationId: organization_id,
    digitalWorker: digital_worker,
    initialSession,
    initialSessions,
    initialProjects,
    onSessionChange: updateUrlForSession,
    onReconnect: () => {
      router.reload({ only: ['session', 'sessions'] })
    },
  })
  const contextUsage = useMemo(
    () => latestContextUsage(chat.activity, chat.session?.messages ?? []),
    [chat.activity, chat.session?.messages],
  )

  useEffect(() => {
    recoveredDraftTokenRef.current = null
    setDraft('')
    setAttachments([])
  }, [digital_worker?.id, initialSession?.id])

  const handleAddAttachments = useCallback((incomingFiles: File[]) => {
    recoveredDraftTokenRef.current = null
    const result = validateAgentChatFiles(attachments, incomingFiles)
    if (result.error) {
      chat.setAttachmentError(result.error)
      return
    }

    setAttachments(result.files)
    chat.setAttachmentError(null)
  }, [attachments, chat.setAttachmentError])

  const handleRemoveAttachment = useCallback((index: number) => {
    recoveredDraftTokenRef.current = null
    setAttachments((current) => current.filter((_, currentIndex) => currentIndex !== index))
    chat.setAttachmentError(null)
  }, [chat.setAttachmentError])

  const handleDraftChange = useCallback((value: string) => {
    recoveredDraftTokenRef.current = null
    setDraft(value)
  }, [])

  const handleSendMessage = async () => {
    const content = draft.trim()
    if (!content) {
      if (attachments.length > 0) chat.setAttachmentError('Escreva uma mensagem para enviar anexos.')
      return
    }
    if (chat.submissionPhase !== 'idle') return

    const recoveryToken = Symbol()
    const selectedAttachments = attachments
    recoveredDraftTokenRef.current = null
    setDraft('')
    setAttachments([])
    await chat.submitMessage(content, selectedAttachments, (pending) => {
      recoveredDraftTokenRef.current = recoveryToken
      setDraft(pending.draft)
      setAttachments(pending.attachments)
    }, () => {
      if (recoveredDraftTokenRef.current !== recoveryToken) return

      recoveredDraftTokenRef.current = null
      setDraft('')
      setAttachments([])
    })
  }

  // assistant-ui composer entry point: its composer owns the text and files, so
  // we receive them directly and run the same shared send core.
  const handleAssistantSubmit = useCallback(async (
    content: string,
    files: File[],
    onRecoveredCommit?: () => void,
    questionAnswer?: UserQuestionAnswer,
  ) => {
    return chat.submitMessage(content, files, undefined, onRecoveredCommit, questionAnswer)
  }, [chat.submitMessage])

  // Publish the live session list to the persistent DashboardLayout sidebar
  // (Chat mode) via the external store. The mounted page is the single source of
  // truth; it clears the store on unmount so the sidebar shows the empty state.
  useEffect(() => {
    publishAgentChatSidebar({
      activeWorkerId: digital_worker?.id ?? null,
      workerName: digital_worker?.name ?? null,
      activeSessionId: chat.isNewConversation ? null : chat.session?.id ?? null,
      isNewConversation: chat.isNewConversation,
      canStartNewConversation: chat.canStartNewConversation,
      contextUsage,
      sessions: chat.sessions,
      projects: chat.projects,
      onNewConversation: chat.handleNewConversation,
      onRenameSession: chat.renameSession,
      onPinSession: chat.pinSession,
      onArchiveSession: chat.archiveSession,
      onDeleteSession: chat.deleteSession,
      onMoveSessionToProject: chat.moveSessionToProject,
      onCreateProject: chat.createProject,
      onRenameProject: chat.renameProject,
      onDeleteProject: chat.deleteProject,
    })
  }, [
    digital_worker,
    chat.session?.id,
    chat.isNewConversation,
    chat.canStartNewConversation,
    contextUsage,
    chat.sessions,
    chat.projects,
    chat.handleNewConversation,
    chat.renameSession,
    chat.pinSession,
    chat.archiveSession,
    chat.deleteSession,
    chat.moveSessionToProject,
    chat.createProject,
    chat.renameProject,
    chat.deleteProject,
  ])

  useEffect(() => () => publishAgentChatSidebar(null), [])

  // When opened from another page (e.g. a Process), `back` returns there;
  // otherwise fall back to the worker page. Only the expected dashboard return
  // targets are honored.
  const rawBack = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('back') : null
  const allowedBackPath = /^\/dashboard\/(?:business_processes|digital_workers)\/[0-9a-f-]+$/i
  const backParam = rawBack && allowedBackPath.test(rawBack) ? rawBack : null
  const backHref = backParam || (digital_worker ? `/dashboard/digital_workers/${digital_worker.id}` : '/dashboard')
  const backLabel = backParam ? 'Voltar' : digital_worker ? `Voltar para ${digital_worker.name}` : 'Voltar'

  // Protótipo chat-first: sem "← Voltar" no header — Esc volta para o início
  // (ou para a origem ?back=, quando o chat foi aberto de um Processo). Só age
  // quando nada consumiu o Esc (menus/dialogs) e fora de campos com conteúdo,
  // para não atropelar renomeação/limpeza de input.
  const escHref = backParam || '/dashboard'
  useEffect(() => {
    if (!useAssistantUi) return undefined
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || target?.isContentEditable) return
      if (tag === 'TEXTAREA' && (target as HTMLTextAreaElement).value.trim() !== '') return
      event.preventDefault()
      router.visit(escHref)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [useAssistantUi, escHref])

  return (
    <>
      <Head title={page_title} />

      {digital_worker ? (
        useAssistantUi ? (
          // Full-bleed thread (SZ-1593): the panel owns the whole content area,
          // with scrolling messages and the composer anchored at the bottom.
          <React.Suspense
            fallback={
              <div
                className="flex h-full items-center justify-center bg-background text-sm text-muted-foreground"
                data-testid="agent-chat-conversation"
              >
                Carregando conversa…
              </div>
            }
          >
            <AssistantConversationPanel
              error={chat.visibleError}
              attachmentError={chat.attachmentError}
              activity={chat.activity}
              isRunning={chat.assistantIsRunning}
              connectionStatus={chat.connectionStatus}
              digitalWorker={digital_worker}
              session={chat.session}
              currentUserName={currentUserName}
              projects={chat.projects}
              onAttachmentError={chat.setAttachmentError}
              onStop={chat.abortCurrentTurn}
              onRetry={chat.retryFailedTurn}
              onFeedback={chat.submitMessageFeedback}
              onBranch={chat.branchConversation}
              onMoveSessionToProject={chat.moveSessionToProject}
              onSubmit={handleAssistantSubmit}
            />
          </React.Suspense>
        ) : (
          // Legacy opt-out (?ui=legacy) keeps the old caged layout untouched.
          <div className="flex h-full min-h-0 flex-col gap-3 p-4 md:p-6">
            <div className="flex items-center justify-between gap-3">
              <Button asChild variant="ghost" size="sm" className="-ml-2 min-w-0">
                <Link href={backHref}>
                  <ArrowLeft className="h-4 w-4 shrink-0" />
                  <span className="truncate">{backLabel}</span>
                </Link>
              </Button>
            </div>

            <div className="min-h-0 flex-1">
              <ConversationPanel
                draft={draft}
                error={chat.visibleError}
                attachmentError={chat.attachmentError}
                attachments={attachments}
                isSending={chat.submissionPhase !== 'idle'}
                isUploadingAttachments={chat.submissionPhase === 'uploading'}
                connectionStatus={chat.connectionStatus}
                digitalWorker={digital_worker}
                session={chat.session}
                currentUserName={currentUserName}
                onDraftChange={handleDraftChange}
                onAddAttachments={handleAddAttachments}
                onRemoveAttachment={handleRemoveAttachment}
                onSendMessage={handleSendMessage}
              />
            </div>
          </div>
        )
      ) : (
        <div
          className="flex h-full min-h-0 flex-col items-center justify-center gap-2 p-8 text-center"
          data-testid="agent-chat-no-worker"
        >
          <div className="flex size-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
            <MessagesSquare className="size-7" aria-hidden="true" />
          </div>
          <h2 className="text-base font-semibold text-foreground">Escolha um colaborador</h2>
          <p className="max-w-sm text-sm text-muted-foreground">
            Selecione um colaborador para ver as conversas dele e começar a conversar.
          </p>
          {(navigableResources?.workers ?? []).length > 0 && (
            <div className="mt-3 flex max-w-md flex-wrap justify-center gap-2">
              {(navigableResources?.workers ?? []).map((worker) => (
                <button
                  key={worker.id}
                  type="button"
                  data-testid={`agent-chat-empty-worker-${worker.id}`}
                  onClick={() => {
                    const params = new URLSearchParams({ digital_worker_id: worker.id })
                    const ui = persistedUiParam()
                    if (ui) params.set('ui', ui)
                    router.visit(`/dashboard/agent_chats?${params.toString()}`)
                  }}
                  className="flex items-center gap-2 rounded-full border border-border bg-card py-1.5 pl-1.5 pr-3 text-sm font-medium text-foreground transition-colors hover:border-brand/60 hover:bg-accent"
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-medium text-primary-foreground">
                    {worker.name?.trim().charAt(0).toUpperCase() || '?'}
                  </span>
                  {worker.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}

AgentChatsIndex.layout = (page: React.ReactElement) => <DashboardLayout variant="fullscreen">{page}</DashboardLayout>

export default AgentChatsIndex
